/**
 * Audio mixer assets (`*.mixer.json`, format `prism.mixer/1`) for the mixer
 * editor: types, parsing, the bus tree and edit helpers. Mirrors
 * `prism_core::audio::MixerDoc`; see engine/docs/systems/audio.md.
 */

export const MIXER_EXT = '.mixer.json';
export const MIXER_FORMAT = 'prism.mixer/1';
export const MASTER = 'Master';
export const MIN_DB = -80;
export const MAX_DB = 20;

export interface BusDesc {
  name: string;
  /** Absent = `Master` (ignored on Master itself). */
  parent?: string;
  /** Linear volume (1 = unchanged); `volumeDb` wins when both are set. */
  volume?: number;
  volumeDb?: number;
  mute?: boolean;
  solo?: boolean;
  [key: string]: unknown;
}

export interface BusOverride {
  volume?: number;
  volumeDb?: number;
  mute?: boolean;
}

export interface SnapshotDesc {
  name: string;
  buses: Record<string, BusOverride>;
}

export interface MixerDoc {
  format: string;
  buses: BusDesc[];
  snapshots?: SnapshotDesc[];
  startSnapshot?: string;
  [key: string]: unknown;
}

export interface ParseResult {
  doc: MixerDoc | null;
  error?: string;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function parseMixer(text: string): ParseResult {
  if (!text.trim()) return { doc: { format: MIXER_FORMAT, buses: [{ name: MASTER, volumeDb: 0 }] } };
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    return { doc: null, error: e instanceof Error ? e.message : String(e) };
  }
  if (!isObject(raw)) return { doc: null, error: 'the file must contain a JSON object' };
  const buses = Array.isArray(raw.buses) ? raw.buses.filter(isObject).map((b) => ({ ...b, name: String(b.name ?? '') }) as BusDesc) : [];
  const snapshots = Array.isArray(raw.snapshots)
    ? raw.snapshots.filter(isObject).map((s) => ({
        ...s,
        name: String(s.name ?? ''),
        buses: isObject(s.buses) ? (s.buses as Record<string, BusOverride>) : {},
      }))
    : undefined;
  return {
    doc: {
      ...raw,
      format: typeof raw.format === 'string' ? raw.format : MIXER_FORMAT,
      buses,
      ...(snapshots ? { snapshots } : {}),
    } as MixerDoc,
  };
}

/** Pretty JSON without empty optional sections. */
export function serializeMixer(doc: MixerDoc): string {
  const out: MixerDoc = { ...doc };
  if (!out.snapshots?.length) delete out.snapshots;
  if (!out.startSnapshot) delete out.startSnapshot;
  return `${JSON.stringify(out, null, 2)}\n`;
}

// ------------------------------------------------------------ decibels

export function dbToLinear(db: number): number {
  return db <= MIN_DB ? 0 : 10 ** (db / 20);
}

export function linearToDb(v: number): number {
  return v <= 0.0001 ? MIN_DB : Math.max(MIN_DB, 20 * Math.log10(v));
}

/** Volume of a bus / override in dB (`volumeDb` wins over `volume`). */
export function volumeDbOf(v: { volume?: number; volumeDb?: number } | undefined): number {
  if (!v) return 0;
  if (typeof v.volumeDb === 'number') return v.volumeDb;
  if (typeof v.volume === 'number') return linearToDb(v.volume);
  return 0;
}

export function formatDb(db: number): string {
  if (db <= MIN_DB) return '-∞ dB';
  return `${db > 0 ? '+' : ''}${db.toFixed(1)} dB`;
}

// ------------------------------------------------------------ bus tree

/** Every bus name including the implicit Master, in file order. */
export function busNames(doc: MixerDoc): string[] {
  const names = [MASTER];
  for (const b of doc.buses) if (b.name && b.name !== MASTER && !names.includes(b.name)) names.push(b.name);
  return names;
}

export function findBus(doc: MixerDoc, name: string): BusDesc | undefined {
  return doc.buses.find((b) => b.name === name);
}

/** The bus entry, creating it (Master may be implicit). */
export function ensureBus(doc: MixerDoc, name: string): BusDesc {
  let b = findBus(doc, name);
  if (!b) {
    b = { name };
    if (name === MASTER) doc.buses.unshift(b);
    else doc.buses.push(b);
  }
  return b;
}

export function parentOf(doc: MixerDoc, name: string): string | null {
  if (name === MASTER) return null;
  const p = findBus(doc, name)?.parent;
  return p && p !== name ? p : MASTER;
}

export function childrenOf(doc: MixerDoc, name: string): string[] {
  return busNames(doc).filter((n) => n !== MASTER && parentOf(doc, n) === name);
}

/** `name` and every bus below it. */
export function descendants(doc: MixerDoc, name: string): Set<string> {
  const out = new Set<string>([name]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const n of busNames(doc)) {
      const p = parentOf(doc, n);
      if (p && out.has(p) && !out.has(n)) {
        out.add(n);
        grew = true;
      }
    }
  }
  return out;
}

export interface TreeRow {
  name: string;
  depth: number;
}

/** Depth-first rows from Master; buses in cycles / with unknown parents are listed at the end under Master. */
export function treeRows(doc: MixerDoc): TreeRow[] {
  const rows: TreeRow[] = [];
  const seen = new Set<string>();
  const walk = (name: string, depth: number) => {
    if (seen.has(name)) return;
    seen.add(name);
    rows.push({ name, depth });
    for (const c of childrenOf(doc, name)) walk(c, depth + 1);
  };
  walk(MASTER, 0);
  for (const n of busNames(doc)) if (!seen.has(n)) walk(n, 1);
  return rows;
}

export function uniqueBusName(doc: MixerDoc, base: string): string {
  const names = new Set(busNames(doc));
  if (!names.has(base)) return base;
  for (let i = 2; ; i++) if (!names.has(`${base} ${i}`)) return `${base} ${i}`;
}

export function uniqueSnapshotName(doc: MixerDoc, base: string): string {
  const names = new Set((doc.snapshots ?? []).map((s) => s.name));
  if (!names.has(base)) return base;
  for (let i = 2; ; i++) if (!names.has(`${base} ${i}`)) return `${base} ${i}`;
}

/** Adds a bus under `parent`; returns its name. */
export function addBus(doc: MixerDoc, parent: string): string {
  const name = uniqueBusName(doc, 'Bus');
  doc.buses.push(parent === MASTER ? { name } : { name, parent });
  return name;
}

/** Removes a bus; its children move to its parent; snapshot overrides of it are dropped. */
export function removeBus(doc: MixerDoc, name: string): void {
  if (name === MASTER) return;
  const parent = parentOf(doc, name) ?? MASTER;
  doc.buses = doc.buses.filter((b) => b.name !== name);
  for (const b of doc.buses) {
    if (b.parent === name) {
      if (parent === MASTER) delete b.parent;
      else b.parent = parent;
    }
  }
  for (const s of doc.snapshots ?? []) delete s.buses[name];
}

/** Moves `name` under `parent` (refused when it would create a cycle). */
export function reparentBus(doc: MixerDoc, name: string, parent: string): boolean {
  if (name === MASTER || descendants(doc, name).has(parent)) return false;
  const b = ensureBus(doc, name);
  if (parent === MASTER) delete b.parent;
  else b.parent = parent;
  return true;
}

/** Renames a bus everywhere in the document (parents, snapshots). */
export function renameBus(doc: MixerDoc, from: string, to: string): boolean {
  const name = to.trim();
  if (from === MASTER || !name || name === from || busNames(doc).includes(name)) return false;
  const b = findBus(doc, from);
  if (!b) return false;
  b.name = name;
  for (const o of doc.buses) if (o.parent === from) o.parent = name;
  for (const s of doc.snapshots ?? []) {
    if (from in s.buses) {
      s.buses[name] = s.buses[from]!;
      delete s.buses[from];
    }
  }
  return true;
}

export function setBusVolumeDb(doc: MixerDoc, name: string, db: number): void {
  const b = ensureBus(doc, name);
  delete b.volume;
  b.volumeDb = round(Math.min(MAX_DB, Math.max(MIN_DB, db)));
}

export function setBusFlag(doc: MixerDoc, name: string, key: 'mute' | 'solo', on: boolean): void {
  const b = ensureBus(doc, name);
  if (on) b[key] = true;
  else delete b[key];
}

// ------------------------------------------------------------ snapshots

export function snapshotOf(doc: MixerDoc, name: string): SnapshotDesc | undefined {
  return doc.snapshots?.find((s) => s.name === name);
}

export function addSnapshot(doc: MixerDoc): string {
  const name = uniqueSnapshotName(doc, 'Snapshot');
  doc.snapshots = [...(doc.snapshots ?? []), { name, buses: {} }];
  return name;
}

export function removeSnapshot(doc: MixerDoc, name: string): void {
  doc.snapshots = (doc.snapshots ?? []).filter((s) => s.name !== name);
  if (doc.startSnapshot === name) delete doc.startSnapshot;
}

export function renameSnapshot(doc: MixerDoc, from: string, to: string): boolean {
  const name = to.trim();
  const s = snapshotOf(doc, from);
  if (!s || !name || name === from || snapshotOf(doc, name)) return false;
  s.name = name;
  if (doc.startSnapshot === from) doc.startSnapshot = name;
  return true;
}

/** Sets / clears one field of a snapshot's override for `bus` (`undefined` clears). */
export function setOverride(doc: MixerDoc, snapshot: string, bus: string, patch: { volumeDb?: number | null; mute?: boolean | null }): void {
  const s = snapshotOf(doc, snapshot);
  if (!s) return;
  const o: BusOverride = { ...(s.buses[bus] ?? {}) };
  if (patch.volumeDb === null) {
    delete o.volumeDb;
    delete o.volume;
  } else if (patch.volumeDb !== undefined) {
    delete o.volume;
    o.volumeDb = round(Math.min(MAX_DB, Math.max(MIN_DB, patch.volumeDb)));
  }
  if (patch.mute === null) delete o.mute;
  else if (patch.mute !== undefined) o.mute = patch.mute;
  if (Object.keys(o).length) s.buses[bus] = o;
  else delete s.buses[bus];
}

function round(v: number): number {
  return Math.round(v * 10) / 10;
}
